import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import BackgroundScene from "../../components/BackgroundScene";
import SEO from "../../components/SEO";
import { usePlausible } from "../../hooks/usePlausible";
import "../../utils/analytics";
import styles from "./Subscribed.module.css";
import { withAssetPath, buildCanonicalUrl, withBasePath } from "../../utils/basePath";

const Subscribed: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const trackEvent = usePlausible();

  useEffect(() => {
    // Track subscription success page view
    trackEvent("Subscription Success Page Viewed");

    // Set loaded state after mount
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, [trackEvent]);

  useEffect(() => {
    if (!isLoaded) return;

    const tl = gsap.timeline();

    // Animate hero section
    tl.to(heroRef.current, {
      opacity: 1,
      duration: 0.8,
      ease: "power2.out",
    })
      // Animate individual hero elements
      .fromTo(
        heroRef.current?.children || [],
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.2, ease: "power2.out" },
        "-=0.6"
      )
      // Animate content section
      .to(
        contentRef.current,
        {
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
        },
        "-=0.4"
      )
      // Animate individual content elements
      .fromTo(
        contentRef.current?.children || [],
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power2.out" },
        "-=0.6"
      );
  }, [isLoaded]);

  const handleDownloadApp = () => {
    trackEvent("Download App from Success Page");
    window.open(withBasePath("/download"), "_blank");
  };

  const handleJoinDiscord = () => {
    trackEvent("Join Discord from Success Page");
    window.open("https://discord.gg/rythm", "_blank");
  };

  const handleViewDashboard = () => {
    trackEvent("View Dashboard from Success Page");
    window.open("https://dashboard.rythm.fm", "_blank");
  };

  return (
    <>
      <SEO
        title="Welcome to Rythm Premium - Subscription Confirmed"
        description="Thank you for subscribing to Rythm Premium! Your subscription is now active. Enjoy unlimited music, 320kbps audio quality, unlimited queue length, and share premium features with everyone in your Discord session."
        canonicalUrl={buildCanonicalUrl("/subscribed")}
        keywords="rythm premium confirmed, premium subscription active, discord music bot premium, subscription success, premium features unlocked"
        schemaType="ConfirmationPage"
        noIndex={true}
        ogImage="https://rythm.fm/assets/images/premium-success-og.png"
        ogType="website"
      />

      <div className={styles.pageContainer}>
        <Header />
        <BackgroundScene />

        <main className={styles.mainContent} ref={containerRef}>
          {/* Hero Section */}
          <section className={styles.heroSection} ref={heroRef}>
            <h1 className={styles.heroTitle}>
              Thanks for joining{" "}
              <span className={styles.gradient}>Rythm Premium!</span>
            </h1>

            <p className={styles.heroSubtitle}>
              Your subscription is now active. Enjoy premium features with
              everyone in the session while you're around.
            </p>
          </section>

          {/* Content Section */}
          <section className={styles.contentSection} ref={contentRef}>
            {/* What's Next */}

            {/* Premium Features Unlocked */}
            <div className={styles.featuresCard}>
              <h2 className={styles.cardTitle}>You've Unlocked</h2>
              <div className={styles.premiumFeatures}>
                {/* All Platforms */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.pinkEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f6.png")}
                      alt="All Platforms"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>All Platforms</h3>
                    <p>
                      Use Rythm Premium on any platform including the website, app,
                      and more.
                    </p>
                  </div>
                </div>

                {/* Higher Audio Quality */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.blueEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f1.png")}
                      alt="Higher Audio Quality"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Higher Audio Quality</h3>
                    <p>
                      (320kbps) Enjoy better audio for everyone using Rythm when you
                      play.
                    </p>
                  </div>
                </div>

                {/* Seamless Crossfade */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.orangeEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f8.png")}
                      alt="Seamless Crossfade"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Seamless Crossfade</h3>
                    <p>
                      Smooth transitions between song for uninterrupted listening.
                    </p>
                  </div>
                </div>

                {/* Audio Effects Studio */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.pinkEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f7.png")}
                      alt="Audio Effects Studio"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Audio Effects Studio</h3>
                    <p>
                      Transform your music with Bass Boost, Speed, Pitch, Nightcore & more.
                    </p>
                  </div>
                </div>

                {/* Unlimited Autoplay */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.yellowEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f3.png")}
                      alt="Unlimited Autoplay"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Unlimited Autoplay</h3>
                    <p>Never run out of songs with autoplay for any playlist.</p>
                  </div>
                </div>

                {/* Unlimited Queue */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.purpleEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f2.png")}
                      alt="Unlimited Queue Length"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Unlimited Queue</h3>
                    <p>No limit on building 1,000+ song queues.</p>
                  </div>
                </div>

                {/* Share Premium */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.greenEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f4.png")}
                      alt="Share Benefits"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Share Premium</h3>
                    <p>
                      Premium is activated for everyone in the sessions while you're
                      around.
                    </p>
                  </div>
                </div>

                {/* Support Artists */}
                <div className={styles.premiumFeature}>
                  <div className={`${styles.ellipse} ${styles.redEllipse}`}></div>
                  <div className={styles.premiumFeatureIcon}>
                    <img
                      className={styles.premiumFeatureIconImg}
                      src={withAssetPath("/assets/images/f5.png")}
                      alt="Support Artists"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.premiumDescription}>
                    <h3>Support Artists</h3>
                    <p>
                      Support your favorite artists and new features across all platforms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Subscribed;
